<template>
  <div>
    <segment-field
      v-model="_value.hasDob"
      class="mb-1"
      :options="[
        { label: $t('components.customer.list.dateOfBirth.hasDOB'), value: 'true' },
        { label: $t('components.customer.list.dateOfBirth.hasNoDOB'), value: 'false' }
      ]"
      outline
      base-class="flex rounded-lg p-1 h-9 focus-within:ring-2"
    />
    <div
      v-if="_value.hasDob === 'true'"
      :class="currentClass"
    >
      <div class="flex flex-grow items-start">
        <div class="w-1/4 flex-shrink-0 text-xs px-3 py-2 text-gray-600 dark:text-gray-300">
          {{ $t('general.date') }}
        </div>
        <div class="flex-grow">
          <select-field
            v-model="_value.day"
            :disabled="_value.hasDob === 'false'"
            :border="false"
            :highlight="false"
            :placeholder="$t('general.any')"
            :options="dayOptions"
          />
        </div>
      </div>

      <div class="flex flex-grow items-start">
        <div class="w-1/4 flex-shrink-0 text-xs px-3 py-2 text-gray-600 dark:text-gray-300">
          {{ $t('datetime.labels.month') }}
        </div>
        <div class="flex-grow">
          <select-field
            v-model="_value.month"
            :disabled="_value.hasDob === 'false'"
            :border="false"
            :highlight="false"
            :placeholder="$t('general.any')"
            :options="monthOptions"
          />
        </div>
      </div>

      <div class="flex flex-grow items-start">
        <div class="w-1/4 flex-shrink-0 text-xs px-3 py-2 text-gray-600 dark:text-gray-300">
          {{ $t('datetime.labels.year') }}
        </div>
        <div class="flex-grow">
          <select-field
            v-model="_value.year"
            :disabled="_value.hasDob === 'false'"
            :border="false"
            :highlight="false"
            :placeholder="$t('general.any')"
            :options="yearOptions"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SelectField, SegmentField } from '@/components/fields'

interface Props {
  modelValue: Record<string, any>
  baseClass?: string | Record<string, any> | any[]
  defaultStatusClass?: string | Record<string, any> | any[]
  warningStatusClass?: string | Record<string, any> | any[]
  errorStatusClass?: string | Record<string, any> | any[]
  successStatusClass?: string | Record<string, any> | any[]
  size?: string | null
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  baseClass: 'border border-gray-200 rounded-md bg-white overflow-hidden divide-y divide-gray-200 focus-within:ring-2 dark:border-gray-800 dark:bg-gray-900 dark:divide-gray-800',
  defaultStatusClass: 'bg-white',
  warningStatusClass: 'border border-yellow-400 bg-yellow-100 dark:bg-yellow-400/20',
  errorStatusClass: 'border border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border border-green-300 bg-green-100 dark:bg-green-400/20',
  size: null,
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const { $dateFns, $date } = useNuxtApp()

const defaultDobValue = () => ({
  day: null,
  month: null,
  year: null,
  hasDob: null,
})

// Mixin logic from classes.js
const noStatus = computed(() => props.status === null)
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isError = computed(() => props.status === false || props.status === 'error')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const result: any[] = []

  if (!noStatus.value && isError.value && props.errorStatusClass) {
    result.push(props.errorStatusClass)
  } else if (!noStatus.value && isSuccess.value && props.successStatusClass) {
    result.push(props.successStatusClass)
  } else if (!noStatus.value && isWarning.value && props.warningStatusClass) {
    result.push(props.warningStatusClass)
  } else if (noStatus.value && props.defaultStatusClass) {
    result.push(props.defaultStatusClass)
  }

  return result
})

const statusName = computed(() => {
  if (isError.value) return 'error'
  if (isSuccess.value) return 'success'
  if (isWarning.value) return 'warning'
  return 'default'
})

const currentClass = computed(() => {
  const result = [props.baseClass]
  return result.concat(statusClasses.value)
})

const _value = computed({
  get () {
    return props.modelValue && typeof props.modelValue === 'object'
      ? props.modelValue
      : defaultDobValue()
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const dayOptions = computed(() => {
  const options: any[] = []
  const days = ($dateFns as any).getDaysInMonth(new Date(_value.value.year ?? 2000, _value.value.month ?? 0))

  for (let i = 1; i <= days; i++) {
    options.push({ text: i, value: i })
  }

  return options
})

const monthOptions = computed(() => {
  const options: any[] = []

  for (let i = 0; i < 12; i++) {
    options.push({ text: ($date as any).format(new Date(0, i), 'MMMM'), value: i })
  }

  return options
})

const yearOptions = computed(() => {
  const options: any[] = []
  const total = 100
  const current = ($dateFns as any).getYear(new Date())

  for (let i = current; i >= current - total; i--) {
    options.push({ text: i, value: i })
  }

  return options
})

const validateDay = () => {
  if (!props.modelValue || typeof props.modelValue !== 'object') {
    return
  }

  const days = ($dateFns as any).getDaysInMonth(new Date(_value.value.year ?? 2000, _value.value.month ?? 0))
  if (_value.value.day > days) {
    _value.value.day = null
  }
}

watch(() => props.modelValue?.month, () => {
  validateDay()
})

watch(() => props.modelValue?.year, () => {
  validateDay()
})

watch(() => props.modelValue?.hasDob, (value) => {
  if (value === 'false') {
    _value.value = { day: null, month: null, year: null, hasDob: value }
  }
})

watch(() => props.modelValue, (value) => {
  if (!value || typeof value !== 'object') {
    _value.value = defaultDobValue()
  }
}, { immediate: true })
</script>
